<script lang="ts">
  import clsx from 'clsx';
  import {
    setContainerContextMaxPixelWidth,
    initializeColorContext,
    getColorContextValue,
  } from '@athena-lib/container-context.svelte';
  import { getMediaType } from '@athena-lib/asset-utils';
  import { Video } from '@athena-lib/Video';
  import { getImageContext } from '@athena-lib/Image/context';
  import type { Responsive } from '@athena-lib/responsive-utils';
  import { deviceNames } from '@athena-lib/device';
  import { objectEntries, objectFromEntries } from '@athena-lib/object-utils';
  import {
    type PageSectionProps,
    getPageSectionWidth,
    getPageWidthClass,
    getPageSectionXPadding,
    getPageSectionXMargin,
  } from './types';
  import {
    getBackgroundClass,
    getBackgroundImageCssVariable,
    getBoxSizingClass,
    getPageSectionYPadding,
  } from './utils';

  const {
    backgroundColor = 'light',
    backgroundMediaUrl,
    backgroundMediaFocalPointClass,
    fullWidthBackgroundMedia = true,
    fullWidthBackgroundColor = true,
    width = 'large',
    children,
    class: className,
    innerClass,
    action = () => undefined,
    actionArg,
    ySpace = 'medium',
    xSpace = 'gutter',
    id,
  }: PageSectionProps = $props();

  const colorContext = initializeColorContext(getColorContextValue(backgroundColor));
  $effect.pre(() => {
    colorContext.value = getColorContextValue(backgroundColor);
  });

  const backgroundMediaType = $derived(
    objectFromEntries(
      deviceNames.map((deviceName) => [
        deviceName,
        getMediaType(
          backgroundMediaUrl
            ? typeof backgroundMediaUrl === 'string'
              ? backgroundMediaUrl
              : backgroundMediaUrl[deviceName]
            : undefined,
        ),
      ]),
    ),
  );

  setContainerContextMaxPixelWidth(getPageSectionWidth(width));

  function elementAction(node: HTMLDivElement) {
    if (action) {
      return action(node, actionArg!);
    }
  }

  const yPaddings = $derived(getPageSectionYPadding(ySpace));
  const xPaddings = $derived.by(() => {
    if (xSpace === 'none') {
      return '';
    }
    return getPageSectionXPadding(width);
  });

  const imageContext = getImageContext();
  const backgroundImageCssVariable = $derived(
    getBackgroundImageCssVariable(backgroundMediaUrl, imageContext),
  );

  const getBackgroundImageClasses = (isOutermost: boolean) => {
    const fullWidthBackgroundMediaResponsive: Responsive<boolean> | undefined =
      typeof fullWidthBackgroundMedia === 'object' ? fullWidthBackgroundMedia : undefined;

    return clsx(
      fullWidthBackgroundMedia === isOutermost &&
        'bg-[image:var(--background-image-url)] md:bg-[image:var(--md-background-image-url)] lg:bg-[image:var(--lg-background-image-url)]',
      typeof fullWidthBackgroundMedia === 'object' &&
        clsx(
          fullWidthBackgroundMediaResponsive?.sm === isOutermost &&
            'bg-[image:var(--background-image-url)]',

          fullWidthBackgroundMediaResponsive?.md === isOutermost &&
            'md:bg-[image:var(--md-background-image-url)]',
          fullWidthBackgroundMediaResponsive?.md !== isOutermost && 'md:bg-[image:unset]',

          fullWidthBackgroundMediaResponsive?.lg === isOutermost &&
            'lg:bg-[image:var(--lg-background-image-url)]',
          fullWidthBackgroundMediaResponsive?.md !== isOutermost && 'lg:bg-[image:unset]',
        ),
    );
  };
</script>

<div
  use:elementAction
  class={clsx(
    className,
    'scroll-mt-36 bg-cover bg-no-repeat',
    getBackgroundClass(backgroundColor, backgroundMediaUrl, fullWidthBackgroundColor, true),
    backgroundMediaFocalPointClass,
    getBackgroundImageClasses(true),
  )}
  {id}
  style={backgroundImageCssVariable}
>
  <div
    class={clsx(
      'mx-auto h-full bg-cover bg-no-repeat',
      getBackgroundClass(backgroundColor, backgroundMediaUrl, fullWidthBackgroundColor, false),
      getBoxSizingClass(
        backgroundImageCssVariable,
        fullWidthBackgroundMedia,
        fullWidthBackgroundColor,
      ),
      backgroundMediaFocalPointClass,
      innerClass,
      getPageWidthClass(width),
      yPaddings,
      xPaddings,
      getPageSectionXMargin(
        width,
        backgroundMediaUrl,
        fullWidthBackgroundMedia,
        fullWidthBackgroundColor,
      ),
      getBackgroundImageClasses(false),
    )}
  >
    {#each objectEntries(backgroundMediaType) as [deviceName, mediaType]}
      {#if mediaType.video}
        <Video
          class={clsx(
            deviceName === 'sm' && 'md:hidden',
            deviceName === 'md' && 'hidden md:block lg:hidden',
            deviceName === 'md' && 'hidden lg:block',
            'absolute left-0 top-0 z-[-1] h-full w-full object-cover',
          )}
          src={typeof backgroundMediaUrl === 'string'
            ? backgroundMediaUrl
            : backgroundMediaUrl![deviceName]}
          autoplay={true}
          muted={true}
          loop={true}
        />
      {/if}
    {/each}
    {#if children}
      {@render children()}
    {/if}
  </div>
</div>
